import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import { useAutosaveStatus } from './useAutosaveStatus';

type SaveDataFn = (opts?: { bypassCheck?: boolean }) => Promise<void> | Promise<boolean> | void;

/**
 * Register the active wizard tab's "flush pending save" handler with the
 * AutosaveStatusContext so the wizard chrome can force an immediate save
 * (the "Finished" button awaits this before navigating away).
 *
 * Each tab already keeps a ``saveDataRef`` (latest save closure) and a
 * ``saveTimeoutRef`` (pending debounce). This hook wires them into a single
 * flush: cancel the debounce, then run the save with ``bypassCheck`` so an
 * in-progress edit is persisted even if the debounce hadn't fired yet. The
 * registration is cleared on unmount, so only the mounted tab is ever active.
 */
export function useRegisterSaveNow(
  saveDataRef: MutableRefObject<SaveDataFn>,
  saveTimeoutRef: MutableRefObject<NodeJS.Timeout | null>,
): void {
  const { registerSaveNow } = useAutosaveStatus();

  useEffect(() => {
    // Tolerate a minimal context (e.g. unit tests that mock useAutosaveStatus
    // with only setStatus/setLastSaveTime). The real wizard provider always
    // supplies registerSaveNow; that wiring is covered by the UploadObject
    // "Finished fires a save" integration test.
    if (typeof registerSaveNow !== 'function') return;
    registerSaveNow(async () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      await saveDataRef.current?.({ bypassCheck: true });
    });
    return () => registerSaveNow(null);
  }, [registerSaveNow, saveDataRef, saveTimeoutRef]);
}
